import React, { useEffect, useMemo, useState, Component } from 'react';
/**
 * FeedbackSplash — Full-screen celebration/encouragement overlay.
 *
 * Shows after answering a question: confetti + Djay for correct,
 * warm floating particles + encouragement for incorrect.
 * Auto-advances after a configurable duration, tap to skip.
 *
 * Optimized s64: particle data + display config extracted to feedbackSplashData.ts
 * (667→~420 lines). Co-located in practice/ domain (CL-1 s64).
 */
import { motion, useReducedMotion } from 'framer-motion';
import { Star, Sparkles } from 'lucide-react';
import { DjayIdentity, getDjaySplashMessage, type DjayTier, type DjayVariant } from '../../../mascot';
import { soundManager } from '../../../utils/soundManager';
import { hapticManager } from '../../../utils/hapticManager';
import { buildConfettiSet, WARM_PARTICLES, XP_COUNTER_STEPS, XP_COUNTER_DURATION_MS, buildSplashDisplayConfig, resolveSplashPriority, type ConfettiParticle, type WarmParticle } from './feedbackSplashData';
// ── Types ────────────────────────────────────────────────────
interface FeedbackSplashProps {
  isCorrect: boolean;
  xpGained: number;
  streak?: number;
  onComplete: () => void;
  levelUp?: {
    level: number;
    xp: number;
    title: string;
  } | null;
  streakMilestone?: {
    count: number;
    message: string;
  } | null;
  dailyGoalReached?: boolean;
  tierEvolution?: {
    from: DjayTier;
    to: DjayTier;
  } | null;
  skinUnlock?: {
    skin: DjayVariant;
    description: string;
  } | null;
  /**
   * Active module's hex color (e.g. '#F97316'). When provided, the confetti
   * palette is derived from this hue so each world celebrates in its own
   * colors. App-Feel §16.E. Omit (or pass undefined) for cross-module / lab
   * sessions to fall back to the generic warm palette.
   */
  moduleColor?: string;
  familyMasteryPercent?: number;
  familyLabel?: string;
}
// ── Shared Sub-components ────────────────────────────────────
function ConfettiLayer({
  particles,
  reduced
}: {
  particles: ConfettiParticle[];
  reduced: boolean | null;
}) {
  if (reduced) return null;
  return <>
      {particles.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.size} pointer-events-none opacity-80`} style={{
      left: `${p.startX}%`,
      top: `${p.startY}%`,
      backgroundColor: p.color
    }} initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      x: `${(p.endX - p.startX) * 2.5}vw`,
      y: `${p.endY - p.startY}vh`,
      scale: [0, 1.2, 1, 0.6],
      opacity: [0, 0.9, 0.9, 0],
      rotate: p.rotation
    }} transition={{
      duration: p.duration,
      delay: p.delay,
      ease: 'easeOut'
    }} data-id="element-10111" />)}
    </>;
}
function WarmParticleLayer({
  particles,
  reduced
}: {
  particles: WarmParticle[];
  reduced: boolean | null;
}) {
  if (reduced) return null;
  return <>
      {particles.map(p => <motion.div key={p.id} className={`absolute rounded-full ${p.size} pointer-events-none opacity-50`} style={{
      left: `${p.startX}%`,
      bottom: '0%',
      backgroundColor: p.color
    }} animate={{
      y: [`${p.startY}vh`, `${p.endY}vh`],
      x: [0, p.drift],
      opacity: [0, 0.5, 0.5, 0]
    }} transition={{
      duration: p.duration,
      delay: p.delay,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-10112" />)}
    </>;
}
// ── Hooks ────────────────────────────────────────────────────
function useXpCounter(target: number) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (target === 0) return;
    const steps = XP_COUNTER_STEPS;
    const increment = target / steps;
    const interval = XP_COUNTER_DURATION_MS / steps;
    let current = 0;
    const id = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDisplay(target);
        soundManager.playTick();
        clearInterval(id);
      } else {
        setDisplay(Math.floor(current));
        soundManager.playTick();
      }
    }, interval);
    return () => clearInterval(id);
  }, [target]);
  return display;
}
// ── Component ────────────────────────────────────────────────
export function FeedbackSplash({
  isCorrect,
  xpGained,
  streak = 0,
  onComplete,
  levelUp,
  streakMilestone,
  dailyGoalReached,
  tierEvolution,
  skinUnlock,
  moduleColor,
  familyMasteryPercent,
  familyLabel
}: FeedbackSplashProps) {
  const shouldReduceMotion = useReducedMotion();
  const xpDisplay = useXpCounter(xpGained);
  const splashMsg = useMemo(() => getDjaySplashMessage(isCorrect, streak), [isCorrect, streak]);
  // App-Feel §16.E — confetti palette derives from the active module's hex.
  // Memoised on moduleColor so it only recomputes when the world changes
  // (typically once per session, not per render).
  const confetti = useMemo(() => buildConfettiSet(moduleColor), [moduleColor]);
  // Play sound and haptic feedback on mount
  useEffect(() => {
    if (levelUp) {
      soundManager.playLevelUp();
      hapticManager.hapticCelebration();
    } else if (dailyGoalReached) {
      soundManager.playGoalReached();
      hapticManager.hapticCelebration();
    } else if (streakMilestone) {
      // F-FEEL-1: dedicated milestone fanfare (vs the plain 2-note streak ding)
      soundManager.playStreakMilestone();
      hapticManager.hapticCelebration();
    } else if (isCorrect) {
      // App-Feel §16.F: pitch-shift on consecutive correct answers.
      // `streak` here is `state.streak.currentTotalStreak` — the session
      // consecutive-correct count, which already resets to 0 on a wrong.
      soundManager.playCorrect(streak ?? 0);
      hapticManager.hapticSuccess();
    } else {
      soundManager.playWrong();
      hapticManager.hapticError();
    }
    // Mount-only: play the splash sound + haptic exactly once per render of
    // the splash. The values read (isCorrect, streak, milestones) are a
    // snapshot of the moment the splash opened — re-firing on prop churn
    // would re-trigger sound/haptic mid-animation.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const config = useMemo(() => buildSplashDisplayConfig({
    isCorrect,
    streak,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
    splashMsg
  }), [isCorrect, streak, levelUp, tierEvolution, skinUnlock, dailyGoalReached, streakMilestone, splashMsg]);
  const priority = useMemo(() => resolveSplashPriority({
    isCorrect,
    streak,
    levelUp,
    tierEvolution,
    skinUnlock,
    dailyGoalReached,
    streakMilestone,
    splashMsg
  }), [isCorrect, streak, levelUp, tierEvolution, skinUnlock, dailyGoalReached, streakMilestone, splashMsg]);
  const {
    mainMessage,
    subMessage,
    emotion,
    duration,
    gradient,
    showExtraConfetti
  } = config;
  const noMotion = !!shouldReduceMotion;
  // Auto-advance timer
  useEffect(() => {
    const timer = setTimeout(onComplete, duration);
    return () => clearTimeout(timer);
  }, [duration, onComplete]);
  // ── Correct Splash ──
  if (isCorrect) {
    return <motion.div initial={{
      opacity: 0,
      scale: noMotion ? 1 : 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: noMotion ? 1 : 1.05
    }} transition={{
      duration: 0.4,
      ease: [0.175, 0.885, 0.32, 1.275] // Custom spring-like ease
    }} onClick={onComplete} className="min-h-[calc(100vh-80px)] w-full flex flex-col items-center justify-center overflow-hidden cursor-pointer relative rounded-3xl" style={{
      background: gradient
    }} data-id="element-10113">
        {/* Radial glow */}
        {!noMotion && <motion.div className="absolute w-[400px] h-[400px] rounded-full pointer-events-none bg-white/60 blur-3xl" initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: [0, 1.5, 1.2],
        opacity: [0, 1, 0.8]
      }} transition={{
        duration: 1.2,
        ease: 'easeOut',
        times: [0, 0.4, 1]
      }} data-id="element-10114" />}

        <ConfettiLayer particles={confetti.main} reduced={shouldReduceMotion} data-id="element-10115" />
        {showExtraConfetti && <ConfettiLayer particles={confetti.extra} reduced={shouldReduceMotion} data-id="element-10116" />}

        {/* Djay Avatar */}
        <motion.div initial={{
        scale: noMotion ? 1 : 0,
        rotate: noMotion ? 0 : -25,
        y: noMotion ? 0 : 60
      }} animate={{
        scale: 1,
        rotate: 0,
        y: noMotion ? 0 : [0, -12, 0, -6, 0]
      }} transition={{
        scale: {
          type: 'spring',
          stiffness: 400,
          damping: 15,
          delay: noMotion ? 0 : 0.1
        },
        rotate: {
          type: 'spring',
          stiffness: 400,
          damping: 15,
          delay: noMotion ? 0 : 0.1
        },
        y: {
          delay: noMotion ? 0 : 0.4,
          duration: noMotion ? 0 : 1.5,
          repeat: noMotion ? 0 : Infinity,
          repeatType: 'reverse',
          ease: 'easeInOut'
        }
      }} className="relative z-10 mb-6" data-id="element-10117">
          <DjayIdentity size="hero" emotion={emotion} animate={!noMotion} data-id="element-10118" />
        </motion.div>

        {/* Main text */}
        <motion.h1 initial={{
        scale: noMotion ? 1 : 0.5,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 30
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 18,
        delay: noMotion ? 0 : 0.25
      }} className="text-4xl sm:text-6xl font-black text-amber-900 mb-4 tracking-tight text-center px-6" style={{
        textShadow: '0 4px 12px rgba(217, 119, 6, 0.2)'
      }} data-id="element-10119">
          {mainMessage}
        </motion.h1>

        {subMessage && <motion.p initial={{
        scale: noMotion ? 1 : 0.8,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: noMotion ? 0 : 0.35
      }} className="text-2xl sm:text-3xl font-bold text-amber-700 mb-8 text-center px-6" data-id="element-10120">
            {subMessage}
          </motion.p>}

        {/* Badges */}
        <motion.div initial={{
        scale: noMotion ? 1 : 0.5,
        opacity: noMotion ? 1 : 0,
        y: noMotion ? 0 : 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        delay: noMotion ? 0 : 0.45,
        type: 'spring',
        stiffness: 350,
        damping: 20
      }} className="flex flex-col items-center gap-4" data-id="element-10121">
          <motion.div whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/90 backdrop-blur-md border-2 border-amber-300 shadow-xl shadow-amber-200/40" data-id="element-10122">
            <motion.div animate={{
            rotate: noMotion ? 0 : [0, 15, -15, 0],
            scale: noMotion ? 1 : [1, 1.2, 1]
          }} transition={{
            duration: noMotion ? 0 : 0.6,
            delay: noMotion ? 0 : 0.6,
            repeat: noMotion ? 0 : Infinity,
            repeatDelay: 2
          }} data-id="element-10123">
              <Star className="w-6 h-6 fill-amber-500 text-amber-500" data-id="element-10124" />
            </motion.div>
            <span className="text-2xl font-black text-amber-800" data-id="element-10125">
              +{xpDisplay} XP
            </span>
          </motion.div>

          {/* Mini Family Mastery Bar */}
          {familyMasteryPercent !== undefined && familyLabel && <motion.div initial={{
          scale: noMotion ? 1 : 0,
          opacity: noMotion ? 1 : 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          delay: noMotion ? 0 : 0.7
        }} className="mt-2 flex flex-col items-center gap-1.5" data-id="element-10126">
              <div className="text-xs font-bold text-amber-800/80 uppercase tracking-wider" data-id="element-10127">
                {familyLabel}
              </div>
              <div className="w-32 h-2 bg-amber-100 rounded-full overflow-hidden shadow-inner" data-id="element-10128">
                <motion.div initial={{
              width: 0
            }} animate={{
              width: `${familyMasteryPercent}%`
            }} transition={{
              duration: 1,
              delay: 0.8,
              ease: 'easeOut'
            }} className="h-full bg-amber-500 rounded-full" data-id="element-10129" />
              </div>
            </motion.div>}

          <div className="flex gap-3 mt-2" data-id="element-10130">
            {streak >= 2 && <motion.div initial={{
            scale: noMotion ? 1 : 0,
            opacity: noMotion ? 1 : 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            type: 'spring',
            delay: noMotion ? 0 : 0.8
          }} className="px-4 py-1.5 rounded-full bg-orange-100 border-2 border-orange-200 text-orange-700 text-sm font-black shadow-sm flex items-center gap-1.5" data-id="element-10131">
                🔥 {streak}x Combo!
              </motion.div>}
            {priority === 'levelUp' && streakMilestone && <div className="px-3 py-1 rounded-full bg-orange-100 border border-orange-200 text-orange-700 text-xs font-bold shadow-sm" data-id="element-10132">
                🔥 {streakMilestone.count} op rij
              </div>}
            {priority === 'levelUp' && dailyGoalReached && <div className="px-3 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 text-xs font-bold shadow-sm" data-id="element-10133">
                ✅ Dagelijks doel
              </div>}
          </div>
        </motion.div>
      </motion.div>;
  }
  // ── Incorrect Splash ──
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.4,
    ease: 'easeInOut'
  }} onClick={onComplete} className="min-h-[calc(100vh-80px)] w-full flex flex-col items-center justify-center overflow-hidden cursor-pointer relative rounded-3xl" style={{
    background: gradient
  }} data-id="element-10134">
      {!noMotion && <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-white blur-3xl pointer-events-none" animate={{
      opacity: [0.1, 0.2, 0.1]
    }} transition={{
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-10135" />}

      <WarmParticleLayer particles={WARM_PARTICLES} reduced={shouldReduceMotion} data-id="element-10136" />

      <motion.div initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 30,
      scale: noMotion ? 1 : 0.85
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1,
      rotate: noMotion ? 0 : [0, -2, 2, -1, 0]
    }} transition={{
      opacity: {
        duration: noMotion ? 0 : 0.5,
        ease: 'easeOut',
        delay: noMotion ? 0 : 0.1
      },
      y: {
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: noMotion ? 0 : 0.1
      },
      scale: {
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: noMotion ? 0 : 0.1
      },
      rotate: {
        delay: noMotion ? 0 : 0.6,
        duration: noMotion ? 0 : 0.8,
        ease: 'easeInOut'
      }
    }} className="relative z-10 mb-6" data-id="element-10137">
        <DjayIdentity size="hero" emotion={emotion} animate={!noMotion} data-id="element-10138" />
      </motion.div>

      <motion.h1 initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.25,
      duration: noMotion ? 0 : 0.4,
      ease: 'easeOut'
    }} className="text-4xl sm:text-5xl font-black text-slate-800 mb-3 tracking-tight text-center px-6" data-id="element-10139">
        {mainMessage}
      </motion.h1>

      <motion.p initial={{
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 15
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.35,
      duration: noMotion ? 0 : 0.4,
      ease: 'easeOut'
    }} className="text-xl sm:text-2xl text-slate-600 font-semibold mb-8 text-center px-6" data-id="element-10140">
        {subMessage}
      </motion.p>

      <motion.div initial={{
      scale: noMotion ? 1 : 0.8,
      opacity: noMotion ? 1 : 0,
      y: noMotion ? 0 : 15
    }} animate={{
      scale: 1,
      opacity: 1,
      y: 0
    }} transition={{
      delay: noMotion ? 0 : 0.45,
      type: 'spring',
      stiffness: 400,
      damping: 25
    }} className="flex flex-col items-center gap-2" data-id="element-10141">
        <div className="flex items-center gap-2.5 px-6 py-3 rounded-full bg-orange-100 border-2 border-orange-200 shadow-[0_8px_30px_rgb(249,115,22,0.2)]" data-id="element-10142">
          <Sparkles className="w-6 h-6 fill-orange-500 text-orange-500" data-id="element-10143" />
          <span className="text-xl font-black text-orange-700" data-id="element-10144">
            +{xpDisplay} XP
          </span>
          <span className="text-sm font-bold text-orange-600 uppercase tracking-wider" data-id="element-10145">
            voor de inzet
          </span>
        </div>
      </motion.div>
    </motion.div>;
}